import { useCurrentUser } from 'context/current-user-context'
import useApi from 'hooks/use-api'
import { FC, ReactNode, useCallback, useEffect, useRef } from 'react'
import { Key, Middleware, SWRConfig, SWRConfiguration, SWRHook } from 'swr'

interface Props {
  children: ReactNode
  opts?: SWRConfiguration
}

// When SWR is revalidating, it's default behavior is to set `data` to undefined.
// This middleware keeps the previous stale data while revalidating.
// See: https://swr.vercel.app/docs/middleware#keep-previous-result
const laggy: Middleware = (useSWRNext: SWRHook) => {
  return (key, fetcher, config) => {
    const laggyDataRef: any = useRef()
    const swr = useSWRNext(key, fetcher, config)

    useEffect(() => {
      if (swr.data !== undefined) {
        laggyDataRef.current = swr.data
      }
    }, [swr.data])

    const resetLaggy = useCallback(() => {
      laggyDataRef.current = undefined
    }, [])

    const dataOrLaggyData =
      swr.data === undefined ? laggyDataRef.current : swr.data
    const isLagging =
      swr.data === undefined && laggyDataRef.current !== undefined

    return Object.assign({}, swr, {
      data: dataOrLaggyData,
      isLagging,
      resetLaggy,
    })
  }
}

// Pass User UUID as an argument with the key. If a user logs
// out and then back in as another user, the old data caches will
// not follow them.
// See: https://swr.vercel.app/docs/arguments#multiple-arguments
const userCacheBuster: Middleware = (useSWRNext: SWRHook) => {
  return (key, fetcher, config) => {
    const { uuid } = useCurrentUser()
    const cacheBusterKey = uuid && key ? [key, uuid] : key
    return useSWRNext(cacheBusterKey, fetcher, config)
  }
}

const Config: FC<Props> = ({ children, opts }) => {
  const api = useApi()

  return (
    <SWRConfig
      value={{
        fetcher: async (key: Key): Promise<any> => await api(key),
        use: [userCacheBuster, laggy],
        onError: (error, key) => {
          if (String(error) === 'Unauthenticated') {
            if (window.location.href !== '/login') {
              window.location.href = '/login'
            }
          }
        },
        ...opts,
      }}>
      {children}
    </SWRConfig>
  )
}

export default Config
